import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #collectionLayout = 'default';
  /** @type {MutationObserver | undefined} */
  #layoutObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    if (this.hasAttribute('data-ow-column-controls')) {
      this.#restoreCollectionLayout();
      this.#layoutObserver = new MutationObserver(() => this.#restoreCollectionLayout());
      this.#layoutObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['product-grid-view'],
      });
    }
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#layoutObserver?.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const isCollection = viewport === 'desktop' && this.hasAttribute('data-ow-column-controls');

    if (isCollection) {
      value = value === 'three-columns' ? value : 'default';
      this.#collectionLayout = value;
      try {
        sessionStorage.setItem('ow-collection-grid-view-desktop', value);
      } catch {}
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    if (isCollection) return;
    requestIdleCallback(() => {
      try {
        sessionStorage.setItem(`product-grid-view-${viewport}`, value);
      } catch {}
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    if (grid.getAttribute('product-grid-view') !== value) {
      grid.setAttribute('product-grid-view', value);
    }
    const name = mediaQueryLarge.matches ? 'grid' : 'grid-mobile';
    for (const option of this.querySelectorAll(`input[name="${name}"]`)) {
      if (option instanceof HTMLInputElement) {
        option.checked = option.value === value;
      }
    }
  }

  #restoreCollectionLayout = () => {
    if (!mediaQueryLarge.matches) return;
    let value = this.#collectionLayout;
    try {
      value = sessionStorage.getItem('ow-collection-grid-view-desktop') || 'default';
    } catch {}
    this.#collectionLayout = value === 'three-columns' ? value : 'default';
    this.#setLayout(this.#collectionLayout);
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    if (event.matches && this.hasAttribute('data-ow-column-controls')) {
      this.#collectionLayout = 'default';
      try {
        sessionStorage.setItem('ow-collection-grid-view-desktop', 'default');
      } catch {}
    }
    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
